import { Button } from '@bambi/component-library/src/components/Button';
import { SimpleTooltip } from '@bambi/component-library/src/components/Tooltip/SimpleTooltip';
import { CheckIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useCallback, useMemo, useState } from 'react';

import { PaymentMethodVerificationStatus } from './PaymentMethodVerificationStatus';
import { VerifyCreditCardIcon } from './VerifyCreditCardIcon';
import { useFeature } from 'src/app/FeatureToggle';
import { defaultCurrencyFormat } from 'src/common/defaultCurrencyFormat';
import type { TilledPaymentMethodResponse } from 'src/common/external/bambi-api/bambiApi';
import {
  useTilledPaymentMethodVerifyCreateMutation,
  useTilledPaymentMethodVerifyRetrieveQuery,
} from 'src/common/external/bambi-api/bambiApi';
import { ConfirmModal } from 'src/common/modals/ConfirmModal';

const VERIFICATION_REFRESH_DELAY = 2000;

type PaymentMethodManageContainerProps = {
  paymentMethod: TilledPaymentMethodResponse;
  handleEditPaymentMethod: (paymentMethod: TilledPaymentMethodResponse) => void;
  handleDeletePaymentMethod: (
    paymentMethod: TilledPaymentMethodResponse
  ) => void;
};

export function PaymentMethodManageContainer({
  paymentMethod,
  handleEditPaymentMethod,
  handleDeletePaymentMethod,
}: PaymentMethodManageContainerProps) {
  const {
    data: verification,
    isLoading,
    isFetching,
    refetch,
  } = useTilledPaymentMethodVerifyRetrieveQuery(
    {
      paymentMethodId: paymentMethod.id,
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const {
    isEnabled: isTilledCardVerificationEnabled,
    isLoading: isLoadingTilledCardVerification,
  } = useFeature('tilled card verification');

  const [verifyPaymentMethod, { isLoading: isVerifyingPaymentMethod }] =
    useTilledPaymentMethodVerifyCreateMutation();
  const [isConfirmingVerification, setIsConfirmingVerification] =
    useState(false);

  const nextFuzzAmount = useMemo(() => {
    return Math.floor(Math.random() * 50);
  }, []);

  const handleVerifyPaymentMethod = useCallback(async () => {
    setIsConfirmingVerification(false);

    await verifyPaymentMethod({
      paymentMethodId: paymentMethod.id,
      tilledPaymentMethodVerifyRequest: {
        fuzz_amount: nextFuzzAmount,
      },
    });
    // The request/response is async, but shouldn't take very long. Attempt to
    // refresh the verification after a short delay once.
    setTimeout(() => {
      refetch();
    }, VERIFICATION_REFRESH_DELAY);
  }, [verifyPaymentMethod, paymentMethod.id, nextFuzzAmount, refetch]);

  const isLoadingVerificationStatus =
    isLoading || isFetching || isVerifyingPaymentMethod;

  const canVerifyPaymentMethod =
    !isLoadingVerificationStatus &&
    (!verification || verification.status === 'failed');

  const canShowVerifyCardFeature =
    !isLoadingTilledCardVerification && isTilledCardVerificationEnabled;

  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-row justify-end">
        {canVerifyPaymentMethod && canShowVerifyCardFeature && (
          <SimpleTooltip
            content={<>Verify Card</>}
            trigger={
              <div>
                <Button
                  variant="ghost-icon"
                  className="cursor-pointer p-1 hover:text-mint"
                  dataTestId="verify-payment-method"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsConfirmingVerification(true);
                  }}
                  ariaLabel="Verify Card"
                >
                  <VerifyCreditCardIcon className="hover:stroke-mint" />
                </Button>
              </div>
            }
          />
        )}
        <Button
          variant="ghost-icon"
          className="cursor-pointer p-1 hover:text-mint"
          dataTestId="edit-payment-method"
          onClick={(e) => {
            e.stopPropagation();
            handleEditPaymentMethod(paymentMethod);
          }}
        >
          <PencilIcon width={24} />
        </Button>
        <Button
          variant="ghost-icon"
          className="cursor-pointer p-1 hover:text-mint"
          dataTestId="delete-payment-method"
          onClick={(e) => {
            e.stopPropagation();
            handleDeletePaymentMethod(paymentMethod);
          }}
        >
          <TrashIcon width={24} />
        </Button>
      </div>
      {canShowVerifyCardFeature && (
        <PaymentMethodVerificationStatus
          verification={verification}
          isLoading={isLoadingVerificationStatus}
        />
      )}
      <ConfirmModal
        open={isConfirmingVerification}
        setOpen={setIsConfirmingVerification}
        onConfirm={handleVerifyPaymentMethod}
        icon={<CheckIcon width={24} />}
        mode="neutral"
        title="Verify Payment Method"
        description={`We will verify this payment method by placing a hold for the amount of ${defaultCurrencyFormat(
          100 + nextFuzzAmount
        )}. This hold will be released automatically within 48 hours.`}
        onCancel={() => setIsConfirmingVerification(false)}
      />
    </div>
  );
}
